import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { RequestService } from './request.service';
import { formatJapaneseAddress } from '../utils/japanese-address';

declare const google: any;

const NOMINATIM_BASE_URL = environment.nominatimBaseUrl;

@Injectable({
  providedIn: 'root'
})
export class GeocodingService {

  constructor(private requestService: RequestService) { }

  /**
   * Resolve a human-readable address for given coordinates.
   * Tries Google Maps Geocoder first (when available), then falls back to Nominatim.
   *
   * @param lat Latitude
   * @param lng Longitude
   * @param language Preferred language for the address (used for Nominatim)
   */
  async resolveAddress(lat: number, lng: number, language: string = 'en'): Promise<string | null> {
    if (lat == null || lng == null) {
      return null;
    }

    // Try Google Maps first
    const googleAddress = await this.geocodeWithGoogle(lat, lng);
    if (googleAddress) {
      return googleAddress;
    }

    // Fallback to Nominatim
    try {
      const address = await this.reverseLookup(lat, lng, language);
      return address;
    } catch {
      return null;
    }
  }

  /**
   * Reverse geocode using Nominatim only (no Google).
   */
  async reverseLookup(lat: number, lng: number, language: string = 'en'): Promise<string | null> {
    if (!NOMINATIM_BASE_URL) {
      return null;
    }

    const params = new URLSearchParams({
      format: 'jsonv2',
      lat: lat.toString(),
      lon: lng.toString(),
      'accept-language': language,
      addressdetails: '1'
    });

    const response = await fetch(`${NOMINATIM_BASE_URL}/reverse?${params.toString()}`, {
      headers: {
        'Accept-Language': language
      }
    });

    if (!response.ok) {
      throw new Error(`Reverse geocode failed with status ${response.status}`);
    }

    const data = await response.json();
    if (data?.address) {
      const japaneseAddress = formatJapaneseAddress(data.address);
      if (japaneseAddress) {
        return japaneseAddress;
      }
      if (this.isCivixOrganizationTemplate()) {
        const flipped = this.formatReversedNominatimAddress(data.address);
        if (flipped) {
          return flipped;
        }
      }
    }
    return data?.display_name || null;
  }

  /** Civix orgs use reversed Nominatim `address` ordering instead of `display_name`. */
  private isCivixOrganizationTemplate(): boolean {
    const org = this.requestService.pageOrganization?.getValue?.();
    if (!org) {
      return false;
    }
    const template = String(org.settings?.template || 'civix').trim().toLowerCase();
    return template === 'civix';
  }

  /**
   * Nominatim `display_name` is usually small→large (e.g. road, town, county, prefecture, postcode, country).
   * This builds the same fields from `address` but in the reversed order (large→small), comma-separated.
   */
  private formatReversedNominatimAddress(address: Record<string, string> | null | undefined): string | null {
    if (!address || typeof address !== 'object') {
      return null;
    }

    const take = (key: string): string | undefined => {
      const v = address[key];
      if (v == null || String(v).trim() === '') {
        return undefined;
      }
      return String(v).trim();
    };

    const parts: string[] = [];
    const push = (value: string | undefined) => {
      if (!value) {
        return;
      }
      if (!parts.includes(value)) {
        parts.push(value);
      }
    };

    // Reverse of typical display_name: … country, postcode, province, county, town, road
    push(take('country'));

    const postcode = take('postcode');
    if (postcode) {
      push(postcode.startsWith('〒') ? postcode : `〒${postcode}`);
    }

    push(take('province'));
    push(take('state'));
    push(take('county'));
    push(take('city'));
    push(take('town'));
    push(take('village'));
    push(take('municipality'));
    push(take('city_district'));
    push(take('suburb'));
    push(take('neighbourhood'));
    push(take('quarter'));
    push(take('road'));
    push(take('pedestrian'));
    push(take('path'));
    push(take('house_number'));
    push(take('house_name'));

    return parts.length > 0 ? parts.join(', ') : null;
  }

  /**
   * Forward geocode using Nominatim (search by text).
   */
  async search(
    text: string,
    language: string = 'en'
  ): Promise<{ lat: number, lng: number, displayName?: string } | null> {
    if (!NOMINATIM_BASE_URL || !text?.trim()) {
      return null;
    }

    const params = new URLSearchParams({
      format: 'jsonv2',
      q: text,
      limit: '1',
      'accept-language': language,
      /** Required for structured `address`; default search omits it. */
      addressdetails: '1'
    });

    const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params.toString()}`, {
      headers: {
        'Accept-Language': language
      }
    });

    if (!response.ok) {
      throw new Error(`Search failed with status ${response.status}`);
    }

    const results = await response.json();
    if (Array.isArray(results) && results.length > 0) {
      const first = results[0];
      const lat = parseFloat(first.lat);
      const lng = parseFloat(first.lon || first.lng);

      if (isFinite(lat) && isFinite(lng)) {
        let displayName: string | undefined = first.display_name;
        const japaneseAddress = first?.address != null ? formatJapaneseAddress(first.address) : null;
        if (japaneseAddress) {
          displayName = japaneseAddress;
        } else if (this.isCivixOrganizationTemplate()) {
          const civixLabel = (first?.address != null ? this.formatReversedNominatimAddress(first.address) : null)
            || await this.reverseLookup(lat, lng, language);
          if (civixLabel) {
            displayName = civixLabel;
          }
        }
        return { lat, lng, displayName };
      }
    }

    return null;
  }

  private isGoogleMapsAvailable(): boolean {
    try {
      return typeof google !== 'undefined' && !!google.maps && !!google.maps.Geocoder;
    } catch {
      return false;
    }
  }

  private geocodeWithGoogle(lat: number, lng: number): Promise<string | null> {
    return new Promise((resolve) => {
      if (!this.isGoogleMapsAvailable() || !environment.shouldWeUseGoogle) {
        resolve(null);
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
        if (status === 'OK' && results && results[0]) {
          resolve(results[0].formatted_address);
        } else {
          resolve(null);
        }
      });
    });
  }
}
